import { useMemo, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useDepartments } from '@/services/httpServices/queries/useDepartments'
import { useDoctors } from '@/services/httpServices/queries/useDoctors'
import { useCreatePublicAppointment } from '@/services/httpServices/queries/usePublicAppointment'
import type { ApiError } from '@/types/api'
import type { Doctor } from '@/types/doctor'

/* ─────────────────────────── date helpers ─────────────────────────── */

const WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
]
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

const APPOINTMENT_TYPES = ['New', 'Follow-up', 'Emergency', 'Procedure', 'Report Review']

function toDateKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`
}

function formatLongDate(dateKey: string): string {
  if (!dateKey) return ''
  const date = new Date(`${dateKey}T00:00:00`)
  return `${WEEKDAYS[date.getDay()]}, ${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`
}

/** Renders a 24h `HH:mm` time as a 12h label with an AM/PM suffix. */
function formatTime12h(value: string): string {
  if (!value) return ''
  const [hoursStr, minutesStr] = value.split(':')
  const hours = Number(hoursStr)
  if (Number.isNaN(hours)) return value
  const suffix = hours >= 12 ? 'PM' : 'AM'
  const hour12 = hours % 12 === 0 ? 12 : hours % 12
  return `${hour12}:${minutesStr ?? '00'} ${suffix}`
}

/** Sentinel day value meaning the doctor is available every weekday. */
const EVERYDAY = 'Everyday'

const isEveryday = (day: string | undefined) =>
  (day || '').toLowerCase() === EVERYDAY.toLowerCase()

const hasTimeRanges = (entry: { timeRanges?: unknown[] } | undefined) =>
  (entry?.timeRanges?.length ?? 0) > 0

/**
 * Consultation availability for the requested date. A schedule configured for
 * the exact weekday always overrides the "Everyday" schedule; "Everyday" only
 * applies to weekdays that have no schedule of their own.
 */
function doctorAvailabilityFor(doctor: Doctor | undefined, dateKey: string) {
  if (!doctor?.consultationAvailability?.length || !dateKey) return undefined
  const weekday = WEEKDAYS[new Date(`${dateKey}T00:00:00`).getDay()]
  return (
    doctor.consultationAvailability.find(
      (entry) => !isEveryday(entry.day) && (entry.day || '').toLowerCase() === weekday.toLowerCase() && hasTimeRanges(entry),
    ) ??
    doctor.consultationAvailability.find(
      (entry) => isEveryday(entry.day) && hasTimeRanges(entry),
    )
  )
}

/**
 * Builds the "Consultation availability" summary. When an "Everyday" schedule is
 * configured it is shown as covering the days that are not overridden by their
 * own weekday schedule, e.g. "Everyday (except Sat) 9:00 AM–5:00 PM · Sat 9:00 AM–12:00 PM".
 */
function buildScheduleSummary(
  doctor: Doctor | undefined,
): Array<{ label: string; ranges: string }> {
  const entries = (doctor?.consultationAvailability ?? []).filter(hasTimeRanges)
  const everyday = entries.find((entry) => isEveryday(entry.day))
  const specificDays = entries.filter((entry) => !isEveryday(entry.day))

  const rangesOf = (entry: (typeof entries)[number]) =>
    entry.timeRanges
      .map((range) => `${formatTime12h(range.from)}–${formatTime12h(range.to)}`)
      .join(', ')

  if (!everyday) {
    return specificDays.map((entry) => ({ label: entry.day, ranges: rangesOf(entry) }))
  }

  const overriddenDays = specificDays
    .map((entry) => entry.day)
    .filter((day) => WEEKDAYS.some((weekday) => weekday.toLowerCase() === (day || '').toLowerCase()))
    .sort((a, b) => WEEKDAYS.indexOf(a) - WEEKDAYS.indexOf(b))

  const label = overriddenDays.length
    ? `${EVERYDAY} (except ${overriddenDays.map((day) => day.slice(0, 3)).join(', ')})`
    : EVERYDAY

  return [
    { label, ranges: rangesOf(everyday) },
    ...specificDays.map((entry) => ({ label: entry.day, ranges: rangesOf(entry) })),
  ]
}

function isDoctorAvailableOn(doctor: Doctor | undefined, dateKey: string): boolean {
  if (!doctor) return false
  return (doctorAvailabilityFor(doctor, dateKey)?.timeRanges?.length ?? 0) > 0
}

/* ─────────────────────────── component ─────────────────────────── */

interface AppointmentFormProps {
  /**
   * Prefix applied to every field id so the same form can be embedded more
   * than once without duplicate DOM ids (e.g. appointment page + homepage).
   */
  idPrefix?: string
}

/**
 * Live appointment booking form shared by the public appointment page and the
 * homepage section. Renders only the `<form>`; callers wrap it in the template's
 * `default-form appointment-form` container so the styling is preserved.
 */
export function AppointmentForm({ idPrefix = 'appt' }: AppointmentFormProps) {
  const [patientName, setPatientName] = useState('')
  const [patientMobile, setPatientMobile] = useState('')
  const [departmentId, setDepartmentId] = useState('')
  const [doctorId, setDoctorId] = useState('')
  const [date, setDate] = useState('')
  const [time, setTime] = useState('')
  const [appointmentType, setAppointmentType] = useState('')
  const [notes, setNotes] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState<{ serial?: number } | null>(null)

  const { data: departmentsData, isLoading: departmentsLoading } = useDepartments()
  const { data: doctorsData, isLoading: doctorsLoading } = useDoctors({
    departmentId: departmentId || undefined,
    status: 'active',
    showOnWebsite: true,
  })
  const createAppointment = useCreatePublicAppointment()

  const departments = useMemo(() => departmentsData?.data ?? [], [departmentsData])
  const doctors = useMemo(() => doctorsData?.data ?? [], [doctorsData])

  const selectedDoctor = useMemo(
    () => doctors.find((d) => d.id === doctorId),
    [doctors, doctorId],
  )

  const todayKey = useMemo(() => toDateKey(new Date()), [])

  const selectedWindows = useMemo(
    () => (date ? doctorAvailabilityFor(selectedDoctor, date)?.timeRanges ?? [] : []),
    [date, selectedDoctor],
  )

  const doctorScheduleSummary = useMemo(
    () => buildScheduleSummary(selectedDoctor),
    [selectedDoctor],
  )

  const mobileDigits = patientMobile.replace(/\D/g, '')
  const mobileInvalid = patientMobile.length > 0 && mobileDigits.length < 6
  const dateUnavailable =
    Boolean(date) && Boolean(selectedDoctor) && !isDoctorAvailableOn(selectedDoctor, date)

  const canSubmit = Boolean(
    patientName.trim() &&
      mobileDigits.length >= 6 &&
      departmentId &&
      doctorId &&
      date &&
      time &&
      appointmentType &&
      !dateUnavailable,
  )

  const handleDepartmentChange = (value: string) => {
    setDepartmentId(value)
    setDoctorId('')
    setDate('')
    setTime('')
  }

  const handleDoctorChange = (value: string) => {
    setDoctorId(value)
    setDate('')
    setTime('')
  }

  const handleDateChange = (value: string) => {
    setDate(value)
    setTime('')
  }

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    setError('')
    if (!canSubmit || createAppointment.isPending) return

    createAppointment.mutate(
      {
        patientName: patientName.trim(),
        patientMobile: patientMobile.trim(),
        departmentId: departmentId || undefined,
        doctorId,
        date,
        time,
        appointmentType,
        notes: notes.trim() || undefined,
      },
      {
        onSuccess: (response) => {
          setSuccess({ serial: response?.data?.serialNumber })
          setPatientName('')
          setPatientMobile('')
          setDepartmentId('')
          setDoctorId('')
          setDate('')
          setTime('')
          setAppointmentType('')
          setNotes('')
        },
        onError: (err) => {
          setError((err as unknown as ApiError)?.message ?? 'Something went wrong. Please try again.')
        },
      },
    )
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="row">
        {error && (
          <div className="col-12">
            <div className="alert alert-danger" role="alert">
              <i className="fa-solid fa-circle-exclamation me-2" aria-hidden="true"></i>
              {error}
            </div>
          </div>
        )}
        {success && (
          <div className="col-12">
            <div className="alert alert-success" role="alert">
              <i className="fa-solid fa-circle-check me-2" aria-hidden="true"></i>
              Appointment booked successfully
              {success.serial ? ` · Serial #${success.serial}` : ''}. We&apos;ll text you a
              confirmation shortly.
            </div>
          </div>
        )}

        {/* Full name */}
        <div className="col-md-6 col-12">
          <div className="form-group">
            <div className="form-floating field-inner">
              <input
                className="form-control"
                id={`${idPrefix}-fullname`}
                name="fullname"
                type="text"
                placeholder="Full Name Here"
                autoComplete="off"
                required
                value={patientName}
                onChange={(e) => setPatientName(e.target.value)}
              />
              <label htmlFor={`${idPrefix}-fullname`}>Full Name *</label>
            </div>
          </div>
        </div>

        {/* Mobile number */}
        <div className="col-md-6 col-12">
          <div className="form-group">
            <div className="form-floating field-inner">
              <input
                className="form-control"
                id={`${idPrefix}-phone`}
                name="phone"
                type="tel"
                placeholder="Mobile Number Here"
                autoComplete="off"
                required
                value={patientMobile}
                onChange={(e) => setPatientMobile(e.target.value)}
              />
              <label htmlFor={`${idPrefix}-phone`}>Mobile Number *</label>
            </div>
            {mobileInvalid && (
              <p className="text-danger small mb-0 mt-1">Enter a valid mobile number.</p>
            )}
          </div>
        </div>

        {/* Department */}
        <div className="col-md-6 col-12">
          <div className="form-group">
            <div className="form-floating">
              <select
                className="form-select"
                id={`${idPrefix}-department`}
                aria-label="Select Department"
                required
                value={departmentId}
                onChange={(e) => handleDepartmentChange(e.target.value)}
              >
                <option value="">
                  {departmentsLoading ? 'Loading departments…' : 'Select Department'}
                </option>
                {departments.map((department) => (
                  <option value={department.id} key={department.id}>
                    {department.name}
                  </option>
                ))}
              </select>
              <label htmlFor={`${idPrefix}-department`}>Select Department *</label>
            </div>
          </div>
        </div>

        {/* Doctor */}
        <div className="col-md-6 col-12">
          <div className="form-group">
            <div className="form-floating">
              <select
                className="form-select"
                id={`${idPrefix}-doctor`}
                aria-label="Choose Doctor"
                required
                disabled={!departmentId || doctorsLoading}
                value={doctorId}
                onChange={(e) => handleDoctorChange(e.target.value)}
              >
                <option value="">
                  {doctorsLoading
                    ? 'Loading doctors…'
                    : departmentId
                      ? 'Choose Doctor'
                      : 'Choose a department first'}
                </option>
                {doctors.map((doctor) => (
                  <option value={doctor.id} key={doctor.id}>
                    {doctor.name}
                    {doctor.designation?.name ? ` · ${doctor.designation.name}` : ''}
                  </option>
                ))}
              </select>
              <label htmlFor={`${idPrefix}-doctor`}>Choose Doctor *</label>
            </div>
          </div>
        </div>

        {/* Doctor availability summary */}
        {selectedDoctor && doctorScheduleSummary.length > 0 && (
          <div className="col-12">
            <p className="small text-muted mb-2">
              <strong>Consultation availability:</strong>{' '}
              {doctorScheduleSummary
                .map((item) => `${item.label} ${item.ranges}`)
                .join(' · ')}
            </p>
          </div>
        )}
        {selectedDoctor && doctorScheduleSummary.length === 0 && (
          <div className="col-12">
            <p className="small text-danger mb-2">
              This doctor has no consultation hours configured yet.
            </p>
          </div>
        )}

        {/* Appointment date */}
        <div className="col-md-6 col-12">
          <div className="form-group">
            <div className="form-floating field-inner">
              <input
                className="form-control"
                id={`${idPrefix}-date`}
                name="appointmentdate"
                type="date"
                min={todayKey}
                autoComplete="off"
                required
                disabled={!doctorId}
                value={date}
                onChange={(e) => handleDateChange(e.target.value)}
              />
              <label htmlFor={`${idPrefix}-date`}>Appointment Date *</label>
            </div>
            {dateUnavailable && (
              <p className="text-danger small mb-0 mt-1">
                {selectedDoctor?.name} is not available on this date. Please pick another day.
              </p>
            )}
          </div>
        </div>

        {/* Appointment time */}
        <div className="col-md-6 col-12">
          <div className="form-group">
            <div className="form-floating">
              <select
                className="form-select"
                id={`${idPrefix}-time`}
                aria-label="Appointment Time"
                required
                disabled={!date || selectedWindows.length === 0}
                value={time}
                onChange={(e) => setTime(e.target.value)}
              >
                <option value="">
                  {!date
                    ? 'Choose a date first'
                    : selectedWindows.length === 0
                      ? 'No time available'
                      : 'Appointment Time'}
                </option>
                {selectedWindows.map((window) => (
                  <option value={window.from} key={`${window.from}-${window.to}`}>
                    {formatTime12h(window.from)}
                    {window.title ? ` · ${window.title}` : ''}
                  </option>
                ))}
              </select>
              <label htmlFor={`${idPrefix}-time`}>Appointment Time *</label>
            </div>
            {date && selectedWindows.length === 0 && !dateUnavailable && (
              <p className="text-danger small mb-0 mt-1">
                No consultation time is available for this day.
              </p>
            )}
          </div>
        </div>

        {/* Appointment type */}
        <div className="col-md-6 col-12">
          <div className="form-group">
            <div className="form-floating">
              <select
                className="form-select"
                id={`${idPrefix}-type`}
                aria-label="Appointment Type"
                required
                value={appointmentType}
                onChange={(e) => setAppointmentType(e.target.value)}
              >
                <option value="">Select Type</option>
                {APPOINTMENT_TYPES.map((type) => (
                  <option value={type} key={type}>
                    {type}
                  </option>
                ))}
              </select>
              <label htmlFor={`${idPrefix}-type`}>Appointment Type *</label>
            </div>
          </div>
        </div>

        {/* Chosen date preview */}
        {date && (
          <div className="col-md-6 col-12">
            <div className="form-group d-flex align-items-center h-100">
              <p className="small text-muted mb-0">
                <i className="fa-regular fa-calendar me-2" aria-hidden="true"></i>
                Selected: <strong>{formatLongDate(date)}</strong>
                {time ? ` at ${formatTime12h(time)}` : ''}
              </p>
            </div>
          </div>
        )}

        {/* Notes */}
        <div className="col-12">
          <div className="form-group">
            <div className="form-floating field-inner">
              <textarea
                id={`${idPrefix}-message`}
                className="form-control"
                name="message"
                placeholder="Message Here"
                autoComplete="off"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              ></textarea>
              <label htmlFor={`${idPrefix}-message`}>Message / Notes</label>
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="appointment-btn-wapper">
            <button
              type="submit"
              className="theme-button style-1"
              data-text="Book Appointment"
              disabled={!canSubmit || createAppointment.isPending}
            >
              <span data-text="Book Appointment">
                {createAppointment.isPending ? 'Booking…' : 'Book Appointment'}
              </span>
              <i className="fa-solid fa-arrow-right"></i>
            </button>
          </div>
        </div>

        <div className="col-12">
          <p className="small text-muted mt-3 mb-0">
            Need to change something later?{' '}
            <Link to="/contact">Contact the hospital reception</Link>.
          </p>
        </div>
      </div>
    </form>
  )
}
